import { updateState } from '@angular-architects/ngrx-toolkit';
import { computed, effect, EffectRef, inject } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { DEFAULT_DOMAIN_SETTINGS, IDomainSettings, TAppDomain } from '@business/settings';
import { TOptional } from '@business/types';
import { coercePercentage } from '@business/utils';
import { signalStore, signalStoreFeature, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { filter } from 'rxjs/operators';
import { withNamedStorage } from '../common';
import { defaultDomainStateConfig, safeSignal } from '../functions';
import { IStateConfig } from '../model';
import { INavigationState, IProgressState, ISessionTimer } from './shell.interface';

export interface IShellState extends INavigationState, IProgressState {
  domainSettings: IDomainSettings;
  sessionTimer: ISessionTimer;
}

export const DEFAULT_SESSION_TIMER: ISessionTimer = {
  timerValue: undefined,
  isSessionTimeout: undefined,
  timestamp: undefined
};

const initialShellState: IShellState = {
  isLoading: false,
  progressValue: 0,
  previousRoute: undefined,
  currentRoute: undefined,
  domainSettings: DEFAULT_DOMAIN_SETTINGS,
  sessionTimer: DEFAULT_SESSION_TIMER
};

const shellStateConfig: IStateConfig = defaultDomainStateConfig({ storeIdentifier: 'shell' });

export const ShellStore = signalStore({ providedIn: 'root' }, withBaseShellStore(initialShellState, shellStateConfig));

function withBaseShellStore(initialShellState: IShellState, { storeIdentifier }: IStateConfig) {
  return signalStoreFeature(
    withState<IShellState>(initialShellState),
    withNamedStorage({ storeIdentifier }),
    withComputed(({ progressValue, previousRoute, sessionTimer }) => {
      return {
        currentProgressValue: computed<number>(() => {
          return coercePercentage(progressValue() ?? initialShellState.progressValue);
        }),
        previousRouteValue: computed<TOptional<TAppDomain>>(() => {
          return safeSignal(previousRoute);
        }),
        timerValue: computed<TOptional<number>>(() => {
          return sessionTimer().timerValue;
        }),
        isSessionTimeout: computed<TOptional<boolean>>(() => {
          return sessionTimer().isSessionTimeout;
        }),
        /**
         * @description this value will change each time an http call finishes or any other time we
         * call resetSessionTimer() (see below) to reset the current value of the session timer
         */
        sessionTimestamp: computed<TOptional<number>>(() => {
          return sessionTimer().timestamp;
        })
      };
    }),
    withMethods((store) => ({
      updateLoading(isLoading: boolean) {
        updateState(store, 'update loading', (state) => ({ ...state, isLoading }));
      },
      updateProgress(progressValue: number) {
        updateState(store, 'update progress', (state) => ({ ...state, progressValue: progressValue }));
      },
      updateNavigation(nextRoute: TAppDomain) {
        updateState(store, 'update navigation', (state) => {
          const updatedState: Partial<INavigationState> = { currentRoute: nextRoute };
          if (nextRoute !== state.currentRoute) {
            updatedState.previousRoute = state.currentRoute;
          }
          return { ...state, ...updatedState };
        });
      },
      resetSessionTimer(timestamp: number = Date.now()) {
        updateState(store, 'reset session timer', (state) => ({
          ...state,
          sessionTimer: {
            ...DEFAULT_SESSION_TIMER,
            timestamp
          }
        }));
      },
      setDomainSettings(domainSettings: IDomainSettings) {
        updateState(store, 'update domain settings', (state) => ({
          ...state,
          domainSettings
        }));
      },
      updateSessionTimer(sessionUpdate: Partial<ISessionTimer>) {
        updateState(store, 'update session timer', (state) => ({
          ...state,
          sessionTimer: {
            ...state.sessionTimer,
            ...sessionUpdate
          }
        }));
      }
    })),
    withHooks((store) => {
      let _navEndEffect: EffectRef | undefined = undefined;

      /** listen to angular's router navigation end event, use it to track previous & current route urls */
      const _navEndEvent = toSignal<NavigationEnd>(
        inject(Router).events.pipe(
          takeUntilDestroyed(),
          filter((event) => event instanceof NavigationEnd)
        )
      );

      return {
        onInit() {
          /** store the latest navigation route url, set the last url as the previous route */
          _navEndEffect = effect(() => {
            const currentNavEnd = _navEndEvent();
            if (currentNavEnd?.url) {
              store.updateNavigation(currentNavEnd?.url as TAppDomain);
            }
          });
        },
        onDestroy() {
          /** stop listening to navigation end event */
          _navEndEffect?.destroy();
        }
      };
    })
  );
}
